import React from "react";
import Images from "../../utils/styles/images";
import messages from "./messages";

/**
 * Cell and header rendering for the NAV tables.
 *
 * Two rules drive everything here:
 *
 * 1. Values are shown as the API sent them. NUMERIC columns (navperunit, price_chg, percent_chg,
 *    percent_inv) hydrate out of postgres as STRINGS and are printed unchanged - parsing them would
 *    drop the stored scale, and four decimal places on a NAV are meaningful. Dates arrive as plain
 *    'yyyy-MM-dd' and timestamps as the stored value formatted in SQL, so neither is re-parsed here
 *    either. (FundList's "NAV DATE" branch splits on "T" and would throw on a bare date string.)
 * 2. Thousands separators are added only to the row counters - the one place this dashboard already
 *    groups digits (RSPAccountDetails/cell_formatter.js) - never to a NAV or a percentage.
 */

/** Columns that are plain integers counted by the backend, safe to group for readability. */
const GROUPED_COUNT_COLUMNS = ["row_count", "rows_affected"];

/** Renders a missing value as a dash so an empty cell cannot be mistaken for an empty string. */
const EMPTY_VALUE = "-";

export function groupDigits(value) {
  const text = String(value);
  if (!/^-?\d+$/.test(text)) {
    return text;
  }
  return text.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function statusClassName(status) {
  const normalised = String(status).toLowerCase();
  if (normalised === "success") {
    return "nav-status-success";
  }
  if (normalised === "failed") {
    return "nav-status-failed";
  }
  return "nav-status-running";
}

export function modifyCell(cell, intl) {
  const columnId = cell && cell.column ? cell.column.id : null;
  const cellValue = cell ? cell.value : null;

  if (cellValue === null || cellValue === undefined || cellValue === "") {
    // A fund with no NAV history has to be unmissable, so a zero row count is a badge, not a 0.
    if (columnId === "row_count") {
      return (
        <div className="nav-badge-no-data">
          <p>{intl.formatMessage(messages.badgeNoData)}</p>
        </div>
      );
    }
    return EMPTY_VALUE;
  }

  if (columnId === "row_count" && Number(cellValue) === 0) {
    return (
      <div className="nav-badge-no-data">
        <p>{intl.formatMessage(messages.badgeNoData)}</p>
      </div>
    );
  }

  if (GROUPED_COUNT_COLUMNS.indexOf(columnId) >= 0) {
    return groupDigits(cellValue);
  }

  if (columnId === "last_status") {
    return (
      <div className={statusClassName(cellValue)}>
        <p>{String(cellValue)}</p>
      </div>
    );
  }

  if (columnId === "detail" || columnId === "list_th" || columnId === "list_eng") {
    // Free text that can be long: keep it on one line with the full value in the tooltip.
    return (
      <span className="nav-cell-truncate" title={String(cellValue)}>
        {String(cellValue)}
      </span>
    );
  }

  // Everything else - NAV values, percentages, dates, timestamps, raw strings - exactly as stored.
  return String(cellValue);
}

// This is just for the header cells
export function modifyCellHeader(column, handleActionHeaderClick, sortBy, sortOrder) {
  if (!column.sortKey || !handleActionHeaderClick) {
    return column.Header;
  }

  const isActive = sortBy === column.sortKey;
  const buttonClassName = isActive
    ? "nav-header-with-sort nav-header-sort-active"
    : "nav-header-with-sort";
  const imageClassName =
    isActive && sortOrder === "desc" ? "nav-sort-icon nav-sort-icon-desc" : "nav-sort-icon";

  return (
    <button
      className={buttonClassName}
      onClick={() => handleActionHeaderClick(column.sortKey)}
    >
      {column.Header}
      <img className={imageClassName} src={Images.sorting.sorting} alt="sorting-icon" />
    </button>
  );
}
